import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { Subject, Subscription, firstValueFrom, merge, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import Swal from 'sweetalert2';
import { UserService } from 'app/services/user.service';
import { AgentSavedChatPage, AgentSavedChatSummary, AgentService } from 'app/services/AIAgent/agent.service';
import { SAVED_CHAT_PATH } from '../ai-agent/ai-agent.component';

// The search box waits this long after the last keystroke before asking the server.
export const SAVED_CHAT_SEARCH_DEBOUNCE_MS = 300;

// EN-5079 - Claire's Saved chats list. The user's own chats in the current client: pinned first, then
// most recent, searchable by title and message text, paged with "Load more". Rename, pin and delete
// act on one chat; delete is permanent (the analysis prompt log is a separate store and is unaffected).
@Component({
	selector: 'app-claire-saved-chats',
	templateUrl: './claire-saved-chats.component.html',
	styleUrls: ['./claire-saved-chats.component.css'],
	imports: [CommonModule, FormsModule, RouterModule],
})
export class ClaireSavedChatsComponent implements OnInit, OnDestroy {
	readonly savedChatPath = SAVED_CHAT_PATH;
	readonly maxTitleLength = 200;

	chats: AgentSavedChatSummary[] = [];
	nextCursor: string | null = null;
	search = '';
	isLoading = false;
	isLoadingMore = false;
	errorMessage: string | null = null;

	editingId: string | null = null;
	editTitle = '';

	private readonly agentService = inject(AgentService);
	private readonly userService = inject(UserService);
	private readonly searchTerms = new Subject<string>();
	private readonly reloads = new Subject<void>();
	private searchSub?: Subscription;

	// Bumped for every first-page load, so a "Load more" that was in flight when the list was replaced
	// (a new search, a pin) cannot append rows from the old list.
	private listGeneration = 0;

	private get clientId(): string {
		return this.userService.currentUserValue?.user?.clientId ?? '';
	}

	ngOnInit(): void {
		// Every first-page load (typing, the initial load, a reload after a pin) goes through one switchMap,
		// which drops an older request when a newer one starts, so the list always matches the box. A
		// failed load is caught inside the switch so the box keeps working for the next keystroke.
		const typed = this.searchTerms.pipe(debounceTime(SAVED_CHAT_SEARCH_DEBOUNCE_MS), distinctUntilChanged());
		const reloaded = this.reloads.pipe(map(() => this.search.trim()));
		this.searchSub = merge(typed, reloaded)
			.pipe(
				switchMap((term) => {
					this.isLoading = true;
					this.listGeneration++;
					return this.agentService
						.listSavedChats(this.clientId, term, null)
						.pipe(catchError(() => of<AgentSavedChatPage | null>(null)));
				})
			)
			.subscribe((page) => (page ? this.showFirstPage(page) : this.showLoadError()));
		this.reload();
	}

	ngOnDestroy(): void {
		this.searchSub?.unsubscribe();
	}

	onSearchChange(term: string): void {
		this.search = term;
		this.searchTerms.next(term.trim());
	}

	// Reloads from the first page with the current search, after a change that can reorder the list.
	reload(): void {
		this.reloads.next();
	}

	async loadMore(): Promise<void> {
		if (!this.nextCursor || this.isLoadingMore) return;
		this.isLoadingMore = true;
		const generation = this.listGeneration;
		try {
			const page = await firstValueFrom(
				this.agentService.listSavedChats(this.clientId, this.search.trim(), this.nextCursor)
			);
			if (generation !== this.listGeneration) return;
			this.chats = [...this.chats, ...page.items];
			this.nextCursor = page.nextCursor;
		} catch {
			this.errorMessage = 'Could not load more chats. Please try again.';
		} finally {
			this.isLoadingMore = false;
		}
	}

	startRename(chat: AgentSavedChatSummary): void {
		this.editingId = chat.id;
		this.editTitle = chat.title;
	}

	cancelRename(): void {
		this.editingId = null;
		this.editTitle = '';
	}

	async saveRename(chat: AgentSavedChatSummary): Promise<void> {
		const title = this.editTitle.trim();
		if (this.editingId !== chat.id) return;
		if (!title || title === chat.title) {
			this.cancelRename();
			return;
		}
		try {
			const updated = await firstValueFrom(this.agentService.updateSavedChat(this.clientId, chat.id, { title }));
			chat.title = updated.title;
			this.errorMessage = null;
		} catch {
			this.errorMessage = 'Could not rename this chat. Please try again.';
		}
		this.cancelRename();
	}

	async togglePin(chat: AgentSavedChatSummary): Promise<void> {
		try {
			await firstValueFrom(this.agentService.updateSavedChat(this.clientId, chat.id, { isPinned: !chat.isPinned }));
			this.errorMessage = null;
			// Pinning moves the chat to the top (or back down), so the order is the server's to give.
			this.reload();
		} catch {
			this.errorMessage = 'Could not update this chat. Please try again.';
		}
	}

	async confirmDelete(chat: AgentSavedChatSummary): Promise<void> {
		// Static text on purpose: SweetAlert renders under document.body, outside this page's
		// data-dd-privacy="mask" container, so a title here would be recorded by session replay.
		const result = await Swal.fire({
			title: 'Delete this chat?',
			text: 'This chat will be removed from Saved chats. This cannot be undone.',
			icon: 'warning',
			showCancelButton: true,
			confirmButtonText: 'Delete',
			cancelButtonText: 'Cancel',
			customClass: {
				confirmButton: 'btn btn-danger',
				cancelButton: 'btn btn-default',
			},
			buttonsStyling: false,
		});
		if (!result.isConfirmed) return;

		try {
			await firstValueFrom(this.agentService.deleteSavedChat(this.clientId, chat.id));
			this.chats = this.chats.filter((c) => c.id !== chat.id);
			this.errorMessage = null;
		} catch {
			this.errorMessage = 'Could not delete this chat. Please try again.';
		}
	}

	trackById(_index: number, chat: AgentSavedChatSummary): string {
		return chat.id;
	}

	private showFirstPage(page: AgentSavedChatPage): void {
		this.chats = page.items;
		this.nextCursor = page.nextCursor;
		this.errorMessage = null;
		this.isLoading = false;
	}

	private showLoadError(): void {
		this.chats = [];
		this.nextCursor = null;
		this.errorMessage = 'Could not load your saved chats. Please try again.';
		this.isLoading = false;
	}
}
